import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView } from "@/lib/analytics";
import { metaByPath, defaultMeta } from "@/config/seoConfig";
import { serviceItems } from "@/data/services";


function setMetaTag(name: string, content: string, property = false): void {
  const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement("meta");
    if (property) element.setAttribute("property", name);
    else element.setAttribute("name", name);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function setCanonical(url: string): void {
  let link = document.head.querySelector<HTMLLinkElement>("link[rel='canonical']");
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", url);
}

function updateBreadcrumbs(pathname: string, name: string): void {
  let script = document.getElementById("breadcrumb-jsonld") as HTMLScriptElement;
  if (!script) {
    script = document.createElement("script");
    script.id = "breadcrumb-jsonld";
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }

  const siteUrl = (import.meta.env.VITE_SITE_URL || "https://craftcivil.co.nz").replace(/\/$/, "");
  const itemListElement: any[] = [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": `${siteUrl}/`
    }
  ];

  if (pathname !== "/") {
    const isServiceDetail = pathname.startsWith("/services/") && pathname.split("/").length > 2;
    const isServiceArea = pathname.startsWith("/service-areas/");
    if (isServiceArea) {
      itemListElement.push({
        "@type": "ListItem",
        "position": 2,
        "name": "Services",
        "item": `${siteUrl}/services`
      });
      itemListElement.push({
        "@type": "ListItem",
        "position": 3,
        "name": "Licensed Drainlaying",
        "item": `${siteUrl}/services/drainlaying`
      });
      itemListElement.push({
        "@type": "ListItem",
        "position": 4,
        "name": name,
        "item": `${siteUrl}${pathname}`
      });
    } else if (isServiceDetail) {
      itemListElement.push({
        "@type": "ListItem",
        "position": 2,
        "name": "Services",
        "item": `${siteUrl}/services`
      });
      itemListElement.push({
        "@type": "ListItem",
        "position": 3,
        "name": name,
        "item": `${siteUrl}${pathname}`
      });
    } else {
      itemListElement.push({
        "@type": "ListItem",
        "position": 2,
        "name": name,
        "item": `${siteUrl}${pathname}`
      });
    }
  }

  const breadcrumbList = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": itemListElement
  };

  script.textContent = JSON.stringify(breadcrumbList, null, 2);
}

function updateFaqSchema(pathname: string): void {
  const isServiceDetail = pathname.startsWith("/services/") && pathname.split("/").length > 2;
  
  if (isServiceDetail) {
    const slug = pathname.split("/").pop();
    const service = serviceItems.find((item: any) => item.slug === slug);
    
    if (service && service.faqs && service.faqs.length > 0) {
      let script = document.getElementById("faq-jsonld") as HTMLScriptElement;
      if (!script) {
        script = document.createElement("script");
        script.id = "faq-jsonld";
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      
      const mainEntity = service.faqs.map((faq: any) => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
        }
      }));
      
      const faqPage = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": mainEntity
      };
      
      script.textContent = JSON.stringify(faqPage, null, 2);
      return;
    }
  }
  
  const script = document.getElementById("faq-jsonld");
  if (script) {
    script.remove();
  }
}

function updateServiceSchema(pathname: string): void {
  const isServiceRoute = pathname.startsWith("/services/") && pathname.split("/").length > 2;
  
  if (isServiceRoute) {
    const slug = pathname.split("/").pop();
    let serviceTitle = "";
    let serviceDesc = "";

    if (slug === "drainage") {
      serviceTitle = "Public & Private Drainage Services";
      serviceDesc = "Comprehensive public and private civil drainage contractors in Auckland. Stormwater networks, sewer connections, and retention systems.";
    } else {
      const service = serviceItems.find((item: any) => item.slug === slug);
      if (service) {
        serviceTitle = service.title;
        serviceDesc = service.shortDesc || service.desc;
      }
    }
    
    if (serviceTitle) {
      let script = document.getElementById("service-jsonld") as HTMLScriptElement;
      if (!script) {
        script = document.createElement("script");
        script.id = "service-jsonld";
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      
      const siteUrl = (import.meta.env.VITE_SITE_URL || "https://craftcivil.co.nz").replace(/\/$/, "");
      const serviceSchema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${siteUrl}${pathname}#service`,
        "name": serviceTitle,
        "description": serviceDesc,
        "url": `${siteUrl}${pathname}`,
        "provider": {
          "@id": `${siteUrl}/#organization`
        },
        "areaServed": [
          { "@type": "City", "name": "Auckland", "sameAs": "https://en.wikipedia.org/wiki/Auckland" }
        ]
      };
      
      script.textContent = JSON.stringify(serviceSchema, null, 2);
      return;
    }
  }

  const isServiceArea = pathname.startsWith("/service-areas/");
  if (isServiceArea) {
    const entry = metaByPath[pathname];
    if (entry) {
      let script = document.getElementById("service-jsonld") as HTMLScriptElement;
      if (!script) {
        script = document.createElement("script");
        script.id = "service-jsonld";
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }

      const siteUrl = (import.meta.env.VITE_SITE_URL || "https://craftcivil.co.nz").replace(/\/$/, "");
      const locationName = entry.breadcrumbName;
      const serviceSchema = {
        "@context": "https://schema.org",
        "@type": "PlumbingContractor",
        "@id": `${siteUrl}${pathname}#contractor`,
        "name": `Craft Civil Contractors - ${locationName}`,
        "description": entry.description,
        "url": `${siteUrl}${pathname}`,
        "telephone": "+64-21-029-84443",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "110 Carlton Street",
          "addressLocality": "Hillsborough",
          "addressRegion": "Auckland",
          "postalCode": "1042",
          "addressCountry": "NZ"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": -36.9189,
          "longitude": 174.7672
        },
        "areaServed": {
          "@type": "AdministrativeArea",
          "name": locationName
        }
      };

      script.textContent = JSON.stringify(serviceSchema, null, 2);
      return;
    }
  }
  
  const script = document.getElementById("service-jsonld");
  if (script) {
    script.remove();
  }
}

export default function RouteSeo() {
  const location = useLocation();

  useEffect(() => {
    // Normalize pathname: strip trailing slash to match metaByPath keys
    // e.g. "/services/drainlaying/" → "/services/drainlaying"
    const normalizedPath = location.pathname === "/"
      ? "/"
      : location.pathname.replace(/\/+$/, "");

    const isKnownRoute = normalizedPath in metaByPath;
    const entry = metaByPath[normalizedPath] || {
      ...defaultMeta,
      title: "Page Not Found | Craft Civil Contractors",
      description: "The page you are looking for does not exist.",
      canonicalPath: normalizedPath,
      breadcrumbName: "404 Not Found",
      robotsDirective: "noindex, nofollow",
    };

    const siteUrl = (import.meta.env.VITE_SITE_URL || "https://craftcivil.co.nz").replace(/\/$/, "");
    const canonicalUrl = `${siteUrl}${entry.canonicalPath}`;
    const absoluteOgImageUrl = `${siteUrl}${entry.ogImage || "/media/asset-01.png"}`;

    document.title = entry.title;
    
    // Core SEO Meta Tags
    setMetaTag("description", entry.description);
    setMetaTag("robots", entry.robotsDirective || "index, follow");
    setCanonical(canonicalUrl);

    // Open Graph / Facebook Meta Tags
    setMetaTag("og:title", entry.title, true);
    setMetaTag("og:description", entry.description, true);
    setMetaTag("og:url", canonicalUrl, true);
    setMetaTag("og:image", absoluteOgImageUrl, true);
    setMetaTag("og:image:width", "1200", true);
    setMetaTag("og:image:height", "630", true);
    setMetaTag("og:image:alt", entry.title, true);
    setMetaTag("og:type", "website", true);
    setMetaTag("og:site_name", "Craft Civil Contractors", true);
    setMetaTag("og:locale", "en_NZ", true);

    // Twitter Card Meta Tags
    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", entry.title);
    setMetaTag("twitter:description", entry.description);
    setMetaTag("twitter:image", absoluteOgImageUrl);

    // Dynamic Schema Injection
    if (isKnownRoute) {
      updateBreadcrumbs(normalizedPath, entry.breadcrumbName);
    } else {
      // Remove breadcrumbs script for 404 pages
      const script = document.getElementById("breadcrumb-jsonld");
      if (script) {
        script.remove();
      }
    }
    updateFaqSchema(normalizedPath);
    updateServiceSchema(normalizedPath);

    trackPageView(normalizedPath);
  }, [location.pathname]);

  return null;
}
